import { useLocation, useSearchParams } from 'react-router'

import {
  PageContextEntity,
  pageContextFor,
  type PageContextRoute,
  type PageContextValue,
} from '../AgentChat/pageContext'

/**
 * Every customer page and the record it hands Ask Cai. Order IS precedence —
 * `claims/new` must precede `claims/:claimId`, which would otherwise swallow it, and likewise
 * `policy-changes/new`.
 *
 * Far fewer records than the staff map carries: most customer pages are about
 * the org the user is already signed into, which the agent's tools resolve for
 * themselves. Only types in `CUSTOMER_ENTITY_RESOLVERS`
 * (`backend/insure/services/agent/ask_cai_agent/page_context.py`) survive the trip —
 * the backend drops the rest and describes the page alone.
 */
export const CUSTOMER_PAGE_CONTEXT_ROUTES: PageContextRoute[] = [
  { pattern: '/insure/insurance', page: 'insurance', label: 'Insurance' },
  { pattern: '/insure/certificates', page: 'certificates', label: 'Certificates' },
  { pattern: '/insure/claims', page: 'claims', label: 'Claims' },
  { pattern: '/insure/claims/new', page: 'claim_new', label: 'New claim' },
  {
    pattern: '/insure/claims/:claimId',
    page: 'claim_detail',
    label: 'Claim',
    entityType: PageContextEntity.CLAIM,
    entityParam: 'claimId',
  },
  { pattern: '/insure/policy-changes', page: 'policy_changes', label: 'Policy changes' },
  { pattern: '/insure/policy-changes/new', page: 'policy_change_new', label: 'New policy change' },
  {
    pattern: '/insure/policy-changes/:requestId',
    page: 'policy_change_detail',
    label: 'Policy change',
    entityType: PageContextEntity.POLICY_CHANGE,
    entityParam: 'requestId',
  },
  { pattern: '/insure/violations', page: 'violations', label: 'Violations' },
  { pattern: '/insure/violations/:id', page: 'violation_detail', label: 'Violation' },
  { pattern: '/insure/documents', page: 'documents', label: 'Documents' },
  { pattern: '/insure/drivers', page: 'drivers', label: 'Drivers' },
  { pattern: '/insure/trucks', page: 'trucks', label: 'Trucks' },
  { pattern: '/insure/trailers', page: 'trailers', label: 'Trailers' },
  { pattern: '/insure/fleet-details', page: 'fleet_details', label: 'Fleet details' },
  { pattern: '/insure/admin-settings', page: 'admin_settings', label: 'Settings' },
]

/**
 * Re-derived on every navigation, like the staff drawer's: the panel stays open
 * while a customer moves around, which is the whole reason page context rides
 * on the message rather than being pinned to the thread.
 */
export function useCustomerPageContext(): PageContextValue | null {
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  return pageContextFor(pathname, searchParams, CUSTOMER_PAGE_CONTEXT_ROUTES)
}
